import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  RespondToAuthChallengeRequest,
  Workspace,
  useRespondToAuthChallengeMutation,
} from '../../api/graphql/generated';
import { useWorkspaceHelpers } from '../../store/workspace/workspace.hooks';
import { useQueryParameters } from '../../util/router';

export class MagicLink {
  public answer?: string | null;
  public challengeName?: string | null;
  public correlationId?: string | null;
  public session?: string | null;
  public username?: string | null;

  public isValid(): boolean {
    const nullValIndex = [
      this.answer,
      this.challengeName,
      this.correlationId,
      this.session,
      this.username,
    ].findIndex((val) => val === undefined || val === null);

    if (nullValIndex === -1) {
      return true;
    }

    return false;
  }
}

export const buildMagicLinkFromSearchParams = (
  params: URLSearchParams
): MagicLink => {
  const link = new MagicLink();

  link.answer = params.get('answer');
  link.challengeName = params.get('challengeName');
  link.correlationId = params.get('correlationId');
  link.session = params.get('session');
  link.username = params.get('username');

  return link;
};

export const useMagicLink = (): MagicLink => {
  const params = useQueryParameters();

  const link = useMemo(() => buildMagicLinkFromSearchParams(params), [params]);

  return link;
};

type ConsumeAuthChallnge = (link: MagicLink) => void;
interface ConsumeAuthChallengeResponse {
  loading: boolean;
  workspaces?: Workspace[];
}

export const useConsumeAuthChallengeLazy = (): [
  ConsumeAuthChallnge,
  ConsumeAuthChallengeResponse,
] => {
  const [respondToChallenge, { data, loading }] =
    useRespondToAuthChallengeMutation({
      fetchPolicy: 'network-only',
    });

  const { upsertWorkspaces } = useWorkspaceHelpers();

  const [workspaces, setWorkspaces] = useState<Workspace[] | undefined>(
    undefined
  );

  const consumeAuthChallenge: ConsumeAuthChallnge = useCallback(
    (link: MagicLink) => {
      if (link.isValid()) {
        const { correlationId, ...otherProps } = link;
        const request = {
          ...otherProps,
        } as RespondToAuthChallengeRequest;

        respondToChallenge({
          context: {
            headers: {
              'X-Correlation-Id': correlationId,
            },
          },
          variables: {
            input: request,
          },
        });
      }
    },
    [respondToChallenge]
  );

  useEffect(() => {
    const run = async () => {
      if (data && !loading) {
        const { respondToAuthChallenge } = data;
        const { workspaces: workspacesToSave } = respondToAuthChallenge;

        if (workspacesToSave.length > 0) {
          await upsertWorkspaces(workspacesToSave as Workspace[]);
        }

        setWorkspaces(workspacesToSave as Workspace[]);
      }
    };

    run();
  }, [data, loading, upsertWorkspaces]);

  return [
    consumeAuthChallenge,
    {
      loading,
      workspaces,
    },
  ];
};
